<script setup>
  import { ref, watch, watchEffect } from 'vue';
  import { useSpotStore } from '@/stores/SpotStore';
  import DataContainer from "@/components/DataContainer.vue";
  import ForecastTable from '@/components/ForecastTable.vue';
  import SegmentedControl from "@/components/controls/SegmentedControl.vue";
  import SegmentedControlButton from "@/components/controls/SegmentedControlButton.vue";  
  import { useCookie } from "@/composables/useCookie.js";
  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();   

  const forecastTable = defineModel('content');
  const emit = defineEmits(['zoomLevel:change', 'dailyMode:change', 'containerSettings:change']);

  const props = defineProps({
    containerSettings: {
      type: Object,
      required: false
    }
  })

  const cookie = useCookie();
  const spotStore = useSpotStore();

  // Persist between sessions using localStorage
  let savedTableSettings = $WEB_VIEW ? null : decodeURIComponent(cookie.get('FxTableSettings'));

  const spotInfo = {
    spotName: spotStore.spotName,
    spotId: spotStore.spotId,
    favSpotId: spotStore.spotFavoriteId,
    lat: spotStore.lat,
    lon: spotStore.lon,
    forecastModelData: {...spotStore.forecastModelData},
  };  

  const forecastTableZoomLevel = ref(1);
  const dailyMode = ref('detailed');
  const selectedModelId = ref(null);

  if (!props.containerSettings && savedTableSettings && savedTableSettings !== 'undefined') {
    savedTableSettings = JSON.parse(savedTableSettings);

    forecastTableZoomLevel.value = parseInt(savedTableSettings.zoom);
    dailyMode.value = savedTableSettings.dailyMode;
  };

  const getSettings = () => forecastTable.value ? forecastTable.value.getSettings() : null;

  watch(forecastTableZoomLevel, (newValue) => { 
    forecastTable.value?.updateZoom(newValue);
    emit('containerSettings:change');
  });

  watch(dailyMode, (newValue) => {  
    forecastTable.value?.updateDailyMode(newValue);
    emit('containerSettings:change');
  });
  
  watchEffect(() => {
    if (props.containerSettings) {
      forecastTableZoomLevel.value = parseInt(props.containerSettings.zoom);
      dailyMode.value = props.containerSettings.dailyMode;
      selectedModelId.value = props.containerSettings.modelId;
    };  
  });

  // need to use watchEffect to ensure the emit is called when component is first mounted
  // watch does not run until the value changes
  watchEffect(() => emit('zoomLevel:change', forecastTableZoomLevel.value));
  watchEffect(() => emit('dailyMode:change', dailyMode.value));

  defineExpose({ getSettings });

</script>

<template>
  <DataContainer style="width: 100%; height: 100%;">
    <div
      class="jwx-data-container__toolbar"
      ref="toolbarContainer"
    >
      <SegmentedControl
        v-model.number="forecastTableZoomLevel"
        label="Select the time period length to display on the forecast table."
        style="--segmented-control-background-color: hsl(360, 100%, 100%); min-width: 160px;"
      >
        <SegmentedControlButton :value="1">Daily</SegmentedControlButton>
        <SegmentedControlButton :value="7">7 Day</SegmentedControlButton>
      </SegmentedControl>

      <SegmentedControl
        v-if="forecastTableZoomLevel === 1"
        v-model="dailyMode"
        label="Choose to display the Basic or Detailed Forecast Table view."
        style="--segmented-control-background-color: hsl(360, 100%, 100%); min-width: 160px; margin-left: auto;"
      >
        <SegmentedControlButton value="basic">Basic</SegmentedControlButton>
        <SegmentedControlButton value="detailed">Detailed</SegmentedControlButton>
      </SegmentedControl>

    </div>

    <ForecastTable
      v-model="forecastTable"
      :spotInfo="spotInfo"
      :selectedModelId="selectedModelId"
      :useSavedState="!$WEB_VIEW"
     />
  </DataContainer>
</template>